/**
 * Offline outbox (ADR-0006). Every change made on the device is written to a
 * persistent outbox first, then sent when there is signal.
 *
 * Guarantees:
 *  - Per-entity FIFO: a survey's operations reach the server in the order they
 *    were made. A stuck operation blocks later ones for the same survey only.
 *  - At-least-once delivery with idempotency keys: the server ignores an
 *    operation id it has already applied, so crashes and retries are safe.
 *  - Autosaves coalesce: only the latest pending draft snapshot is kept, and
 *    it moves to the back of the queue so it follows any photo uploads it
 *    references.
 *  - Retries back off exponentially with jitter. Rejections (e.g. the draft
 *    was taken over) stop that survey and are surfaced to the user.
 *  - A permanent refusal that the app has already dealt with (a file the
 *    server will never accept, a discard the server no longer needs) is
 *    `drop`ped: removed, reported, and the survey's queue carries on.
 */

export type OutboxOp = 'survey.upsertDraft' | 'survey.submit' | 'survey.resubmit' | 'survey.discard' | 'survey.checkpointScan' | 'attachment.upload' | 'event.report';

/** Hazard and incident reports (ADR-0029): one self-contained op per report, never coalesced. */
export function isEventOp(op: OutboxOp): boolean {
  return op.startsWith('event.');
}

export interface OutboxItem<P = unknown> {
  /** Idempotency key. */
  id: string;
  entityId: string;
  op: OutboxOp;
  payload: P;
  createdAt: number;
  seq: number;
  attempts: number;
  nextAttemptAt: number;
  state: 'PENDING' | 'REJECTED';
  lastError?: string;
}

export interface OutboxStore {
  all(): Promise<OutboxItem[]>;
  put(item: OutboxItem): Promise<void>;
  remove(id: string): Promise<void>;
}

export type SendResult =
  | { status: 'ok' }
  | { status: 'retry'; error: string }
  | { status: 'reject'; error: string }
  /** Refused for good, but handled by the app: remove it and let the rest of the queue go on. */
  | { status: 'drop'; error: string };

export interface Transport {
  send(item: OutboxItem): Promise<SendResult>;
}

export interface SyncOptions {
  now?: () => number;
  random?: () => number;
  newId?: () => string;
  baseDelayMs?: number;
  maxDelayMs?: number;
}

export interface FlushReport {
  sent: string[];
  retrying: string[];
  rejected: string[];
  dropped: string[];
  blockedEntities: string[];
}

export interface DrainOptions {
  /** Give up after this long (wall clock). */
  timeoutMs: number;
  /** Wait between passes while something is still retrying. */
  pauseMs?: number;
  /** Injected for tests. */
  sleep?: (ms: number) => Promise<void>;
}

export interface DrainReport {
  /** Items still in the outbox (pending or rejected) when the drain stopped. */
  remaining: number;
  passes: number;
  timedOut: boolean;
}

export interface OutboxStats {
  pending: number;
  rejected: number;
  oldestPendingAgeMs: number | null;
  byEntity: Record<string, number>;
}

/** In-memory store for tests and for the server-side simulator. */
export class MemoryOutboxStore implements OutboxStore {
  readonly items = new Map<string, OutboxItem>();
  async all() {
    return [...this.items.values()].map((i) => structuredClone(i));
  }
  async put(item: OutboxItem) {
    this.items.set(item.id, structuredClone(item));
  }
  async remove(id: string) {
    this.items.delete(id);
  }
}

export class Outbox {
  private readonly now: () => number;
  private readonly random: () => number;
  private readonly newId: () => string;
  private readonly baseDelayMs: number;
  private readonly maxDelayMs: number;
  private flushing: Promise<FlushReport> | null = null;

  constructor(
    private readonly store: OutboxStore,
    private readonly transport: Transport,
    opts: SyncOptions = {},
  ) {
    this.now = opts.now ?? (() => Date.now());
    this.random = opts.random ?? Math.random;
    this.newId = opts.newId ?? (() => crypto.randomUUID());
    this.baseDelayMs = opts.baseDelayMs ?? 2_000;
    this.maxDelayMs = opts.maxDelayMs ?? 10 * 60_000;
  }

  async enqueue<P>(op: OutboxOp, entityId: string, payload: P): Promise<OutboxItem<P>> {
    const items = await this.store.all();
    if (op === 'survey.upsertDraft') {
      // Drop older unsent snapshots of this draft; the new one supersedes them.
      for (const i of items) {
        if (i.entityId === entityId && i.op === 'survey.upsertDraft' && i.state === 'PENDING' && i.attempts === 0) {
          await this.store.remove(i.id);
        }
      }
    }
    const seq = items.reduce((m, i) => Math.max(m, i.seq), 0) + 1;
    const item: OutboxItem<P> = {
      id: this.newId(),
      entityId,
      op,
      payload,
      createdAt: this.now(),
      seq,
      attempts: 0,
      nextAttemptAt: 0,
      state: 'PENDING',
    };
    await this.store.put(item);
    return item;
  }

  /** Send everything that is due. Concurrent calls share one flush. */
  flush(): Promise<FlushReport> {
    if (!this.flushing) {
      this.flushing = this.doFlush().finally(() => {
        this.flushing = null;
      });
    }
    return this.flushing;
  }

  private backoff(attempts: number): number {
    const exp = Math.min(this.maxDelayMs, this.baseDelayMs * 2 ** (attempts - 1));
    return Math.round(exp / 2 + this.random() * (exp / 2));
  }

  private async doFlush(): Promise<FlushReport> {
    const report: FlushReport = { sent: [], retrying: [], rejected: [], dropped: [], blockedEntities: [] };
    const items = (await this.store.all()).sort((a, b) => a.seq - b.seq);
    const blocked = new Set<string>();
    const now = this.now();
    // A rejection or drop may make the app remove other queued items of that
    // survey (e.g. a submit that must be redone): never send those afterwards.
    let live: Set<string> | null = null;

    for (const item of items) {
      if (blocked.has(item.entityId)) continue;
      if (live && !live.has(item.id)) continue;
      if (item.state === 'REJECTED' || item.nextAttemptAt > now) {
        blocked.add(item.entityId);
        continue;
      }
      let result: SendResult;
      try {
        result = await this.transport.send(item);
      } catch (e) {
        result = { status: 'retry', error: e instanceof Error ? e.message : String(e) };
      }
      if (result.status === 'ok') {
        await this.store.remove(item.id);
        report.sent.push(item.id);
        continue;
      }
      if (result.status === 'drop') {
        await this.store.remove(item.id);
        report.dropped.push(item.id);
        live = new Set((await this.store.all()).map((i) => i.id));
        continue;
      }
      blocked.add(item.entityId);
      const current = new Set((await this.store.all()).map((i) => i.id));
      // Removed while it was on its way (e.g. the survey was discarded): never put it back.
      if (!current.has(item.id)) {
        live = current;
        continue;
      }
      const updated: OutboxItem = { ...item, attempts: item.attempts + 1, lastError: result.error };
      if (result.status === 'retry') {
        updated.nextAttemptAt = now + this.backoff(updated.attempts);
        report.retrying.push(item.id);
      } else {
        updated.state = 'REJECTED';
        report.rejected.push(item.id);
      }
      await this.store.put(updated);
      if (result.status === 'reject') live = current;
    }
    report.blockedEntities = [...blocked];
    return report;
  }

  /** User chose to discard a rejected operation (and everything after it for that survey). */
  async discardEntity(entityId: string): Promise<number> {
    const items = (await this.store.all()).filter((i) => i.entityId === entityId);
    for (const i of items) await this.store.remove(i.id);
    return items.length;
  }

  /** Retry a rejected or backed-off entity immediately (e.g. after re-taking the lock). */
  async retryEntity(entityId: string): Promise<void> {
    for (const i of await this.store.all()) {
      if (i.entityId === entityId) await this.store.put({ ...i, state: 'PENDING', nextAttemptAt: 0 });
    }
  }

  /** Make every item due now, rejected ones included (one more attempt each). */
  async resetBackoff(): Promise<void> {
    for (const i of await this.store.all()) {
      if (i.state !== 'PENDING' || i.nextAttemptAt !== 0) await this.store.put({ ...i, state: 'PENDING', nextAttemptAt: 0 });
    }
  }

  /**
   * Send until the outbox is empty, nothing can make progress, or time runs
   * out — used before the device is wiped (ADR-0021), so a backed-off or
   * once-rejected item still gets its chance. Items keep their order, and a
   * rejection still stops its survey.
   */
  async drain(opts: DrainOptions): Promise<DrainReport> {
    const start = this.now();
    const sleep = opts.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
    const pause = opts.pauseMs ?? 1_000;
    await this.resetBackoff();
    let passes = 0;
    for (;;) {
      const report = await this.flush();
      passes++;
      const left = await this.store.all();
      if (left.length === 0) return { remaining: 0, passes, timedOut: false };
      const progressed = report.sent.length + report.dropped.length > 0;
      // A rejected item stops its survey; only other surveys can still move.
      const stuck = new Set(left.filter((i) => i.state === 'REJECTED').map((i) => i.entityId));
      const retrying = left.some((i) => i.state === 'PENDING' && !stuck.has(i.entityId));
      if (!progressed && !retrying) return { remaining: left.length, passes, timedOut: false };
      if (this.now() - start >= opts.timeoutMs) return { remaining: left.length, passes, timedOut: true };
      if (!progressed) await sleep(pause);
      // Retries are due again at once: the wipe is waiting, not the backoff.
      for (const i of left) if (i.state === 'PENDING' && i.nextAttemptAt !== 0) await this.store.put({ ...i, nextAttemptAt: 0 });
    }
  }

  async stats(): Promise<OutboxStats> {
    const items = await this.store.all();
    const pending = items.filter((i) => i.state === 'PENDING');
    const now = this.now();
    const byEntity: Record<string, number> = {};
    for (const i of items) byEntity[i.entityId] = (byEntity[i.entityId] ?? 0) + 1;
    return {
      pending: pending.length,
      rejected: items.length - pending.length,
      oldestPendingAgeMs: pending.length ? now - Math.min(...pending.map((i) => i.createdAt)) : null,
      byEntity,
    };
  }
}
